import { useState, useRef, useEffect, isValidElement, cloneElement } from "react";
import { cn } from "@/lib/utils";

interface DropdownMenuProps {
  trigger: React.ReactNode;
  children: React.ReactNode;
  align?: "left" | "right";
  side?: "top" | "bottom"; // top = open upwards, bottom = open downwards
  className?: string; // extra classes for the wrapper
  triggerAsChild?: boolean; // use the provided trigger element directly (no extra button)
  contentClassName?: string; // extra classes for the dropdown content container
}

export function DropdownMenu({ trigger, children, align = "right", side = "top", className, triggerAsChild = false, contentClassName }: DropdownMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div className={cn("relative", className)} ref={dropdownRef}>
      {triggerAsChild && isValidElement(trigger) ? (
        cloneElement(trigger as any, {
          onClick: (e: any) => {
            e?.preventDefault?.();
            e?.stopPropagation?.();
            setIsOpen((v) => !v);
          },
        })
      ) : (
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center justify-center w-8 h-8 rounded-full hover:bg-muted transition-colors"
        >
          {trigger}
        </button>
      )}
      
      {isOpen && (
        <div
          className={cn(
            "absolute bg-background border border-border rounded-md shadow-lg z-50 w-64",
            side === "top" ? "bottom-full mb-2" : "top-full mt-2",
            align === "right" ? "right-0" : "left-0",
            contentClassName
          )}
        >
          <div className="py-1">
            {children}
          </div>
        </div>
      )}
    </div>
  );
}

interface DropdownMenuItemProps {
  onClick?: () => void;
  children: React.ReactNode;
  className?: string;
}

export function DropdownMenuItem({ onClick, children, className }: DropdownMenuItemProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "w-full px-4 py-2 text-left text-sm hover:bg-muted transition-colors flex items-center gap-2",
        className
      )}
    >
      {children}
    </button>
  );
}
